import { TableFilters } from '@/shared/ui/table';
import { TanstackTableProps } from '@/shared/ui/table/table.types';

import type { BuildTablePropsArgs } from './table.types';
import { TABLE_MODE } from './table-schema';

export const buildTableProps = <TRow, TFilters>({
	schema,
	state,
	data,
	filters,
	total: totalProp,
	totalPages: totalPagesProp,
	isLoading = false,
}: BuildTablePropsArgs<TRow, TFilters>): TanstackTableProps<TRow> => {
	const searchEnabled = schema.search?.enabled ?? false;
	const sortingEnabled = schema.sorting?.enabled ?? false;
	const paginationEnabled = schema.pagination?.enabled ?? false;
	const mode = schema.mode ?? TABLE_MODE.SERVER;
	const manualSorting = mode === TABLE_MODE.SERVER || Boolean(schema.client?.sort);
	const manualPagination = mode === TABLE_MODE.SERVER;

	const modeHandlers = {
		server: () => ({
			rows: data,
			total: totalProp,
			totalPages: totalPagesProp,
		}),
		client: () => {
			let clientRows = data;

			const filterFn = schema.client?.filter;
			const searchFn = schema.client?.search;
			const sortFn = schema.client?.sort;

			if (filterFn) {
				clientRows = filterFn(clientRows, filters);
			}

			if (searchEnabled && state.debouncedSearch && searchFn) {
				clientRows = clientRows.filter((row) => searchFn(row, state.debouncedSearch));
			}

			if (sortingEnabled && sortFn) {
				clientRows = sortFn(clientRows, state.sort);
			}

			const total = clientRows.length;
			const totalPages = paginationEnabled ? Math.max(1, Math.ceil(total / state.pageSize)) : 1;
			const rows = clientRows;

			return { rows, total, totalPages };
		},
	};

	const { rows, total, totalPages } = modeHandlers[mode]();

	const basePage =
		mode === TABLE_MODE.CLIENT && paginationEnabled ? Math.min(state.page, totalPages ?? state.page) : state.page;
	const schemaFilters = schema.filters;
	const filtersConfig = schemaFilters?.config?.filter((filter) => filter.enabled !== false);

	return {
		...schema.tableProps,
		data: rows,
		columns: schema.columns,
		getRowId: schema.getRowId,
		isLoading,
		enableSorting: sortingEnabled,
		sort: state.sort,
		manualSorting,
		manualPagination,
		...(sortingEnabled && { onChangeSort: state.onChangeSort }),
		...(paginationEnabled && { onChangePage: state.onChangePage, onPageSizeChange: state.onChangePageSize }),
		page: basePage,
		pageSize: state.pageSize,
		total,
		totalPages,
		showPagination: paginationEnabled,
		filters: (
			<TableFilters
				filters={filtersConfig}
				onChange={schemaFilters?.onChange}
				containerProps={schemaFilters?.containerProps}
				wrapperProps={schemaFilters?.wrapperProps}
				fixedFilters={schemaFilters?.fixedFilters}
				toggle={schemaFilters?.toggle}
			/>
		),
	};
};
